/* istanbul ignore file */
import { DiscoveryApi, FetchApi } from "@backstage/core-plugin-api";
import { UploadValidation } from "./types";
import {
  AgentsListResponse,
  getStatusGetSystemStatusUrl,
  AuditGetAuditLogsParams,
  AuditLogResponse,
  getAuditGetAuditLogsUrl,
  TasksListResponse,
  getTasksGetAllTasksUrl,
  getTasksApproveTasksUrl,
  getHrUploadSapFileUrl,
  FileUploadResponse,
  getTasksRejectTasksUrl,
  getNotificationsGetNotificationsUrl,
  NotificationsListResponse,
  NotificationsGetNotificationsParams,
  TasksGetPeriodicTasksParams,
  getTasksGetPeriodicTasksUrl,
  PeriodicTasksListResponse,
  getTasksStartPeriodicTaskUrl,
  getTasksCompletePeriodicTaskUrl,
  getPolicyListExecutableRulesUrl,
  ExecutableRulesListResponse,
  getStatisticsGetCompliancyStatisticsUrl,
  StatisticsCompliancyResponse,
  PeriodicRulesListResponse,
  getPolicyListPeriodicRulesUrl,
  RemediationTask,
  getTasksGetTaskByIdUrl,
  TasksGetAllTasksParams,
  PolicyExecutableRule,
  getPolicyGetExecutableRuleUrl,
  getPolicyUpdateExecutableRuleUrl,
  ExecutableRuleUpdate,
  PeriodicRuleUpdate,
  PolicyPeriodicRule,
  getPolicyUpdatePeriodicRuleUrl,
  getPolicyGetPeriodicRuleUrl,
  PeriodicTask,
  getTasksGetPeriodicTaskByIdUrl,
  getStatisticsGetTaskTotalsUrl,
  StatisticsTaskTotals,
} from "stan-client";
import { FileWithPath } from "react-dropzone";
import { handleResponse, handleVoidResponse } from "./utils/handle-response";

const normalizeUrl = (str: string) => str.replace("/api", "");

// TODO: try to fit generated Orval fetch inside Backstage client
// Orval doesn't easily allow dynamic fetch instances, only static ones
// Backstage has its own fetch wrapper, and dynamically gets api base urls
// This poses an interop problem that's non-trivial to fix
// Backstage offers an OpenAPI generator but it requires Java to be installed
// For now we still generate the API client, then use the typings and copy paste
// the relevant code sections into this client.

export class StanClient {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return `${await this.discoveryApi.getBaseUrl("stan")}`;
  }

  async agents(): Promise<AgentsListResponse> {
    const baseUrl = await this.getBaseUrl();
    const path = normalizeUrl(getStatusGetSystemStatusUrl());
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`);
    return handleResponse(response);
  }

  async logs(params?: AuditGetAuditLogsParams): Promise<AuditLogResponse> {
    const baseUrl = await this.getBaseUrl();
    const path = normalizeUrl(getAuditGetAuditLogsUrl(params));
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`);
    return handleResponse(response);
  }

  async tasks(params?: TasksGetAllTasksParams): Promise<TasksListResponse> {
    const baseUrl = await this.getBaseUrl();
    const path = normalizeUrl(getTasksGetAllTasksUrl(params));
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`);
    return handleResponse(response);
  }

  async taskById(id: string): Promise<RemediationTask> {
    const baseUrl = await this.getBaseUrl();
    const path = normalizeUrl(getTasksGetTaskByIdUrl(id));
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`);
    return handleResponse(response);
  }

  async approveTasks(ids: string[]): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const path = normalizeUrl(getTasksApproveTasksUrl());

    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        task_ids: ids,
      }),
    });

    handleVoidResponse(response);
  }

  async rejectTasks(ids: string[]): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const path = normalizeUrl(getTasksRejectTasksUrl());

    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        task_ids: ids,
      }),
    });

    return handleVoidResponse(response);
  }

  async executableRules(): Promise<ExecutableRulesListResponse> {
    const baseUrl = await this.getBaseUrl();
    const path = normalizeUrl(getPolicyListExecutableRulesUrl());
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`);
    return handleResponse(response);
  }

  async executableRulesById(id: string): Promise<PolicyExecutableRule> {
    const baseUrl = await this.getBaseUrl();
    const path = normalizeUrl(getPolicyGetExecutableRuleUrl(id));
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`);
    return handleResponse(response);
  }

  async executableRulesUpdate(
    id: string,
    update: ExecutableRuleUpdate
  ): Promise<PolicyExecutableRule> {
    const baseUrl = await this.getBaseUrl();
    const path = normalizeUrl(getPolicyUpdateExecutableRuleUrl(id));
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(update),
    });
    return handleResponse(response);
  }

  async periodicRules(): Promise<PeriodicRulesListResponse> {
    const baseUrl = await this.getBaseUrl();
    const path = normalizeUrl(getPolicyListPeriodicRulesUrl());
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`);
    return handleResponse(response);
  }

  async periodicRulesById(id: string): Promise<PolicyPeriodicRule> {
    const baseUrl = await this.getBaseUrl();
    const path = normalizeUrl(getPolicyGetPeriodicRuleUrl(id));
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`);
    return handleResponse(response);
  }

  async periodicRulesUpdate(
    id: string,
    update: PeriodicRuleUpdate
  ): Promise<PolicyPeriodicRule> {
    const baseUrl = await this.getBaseUrl();
    const path = normalizeUrl(getPolicyUpdatePeriodicRuleUrl(id));
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(update),
    });
    return handleResponse(response);
  }

  // TODO: Add to API
  async hrUploadValidate(file: FileWithPath): Promise<UploadValidation> {
    const baseUrl = await this.getBaseUrl();

    const formData = new FormData();
    formData.append("file", file);
    formData.append("fileName", file.name);

    const response = await this.fetchApi.fetch(
      `${baseUrl}/hr/upload/validate`,
      {
        method: "POST",
        body: formData,
      }
    );

    return handleResponse(response);
  }

  async hrUploadFile(file: FileWithPath): Promise<FileUploadResponse> {
    const baseUrl = await this.getBaseUrl();
    const path = normalizeUrl(getHrUploadSapFileUrl());

    const formData = new FormData();
    formData.append("file", file);
    formData.append("fileName", file.name);

    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      method: "POST",
      body: formData,
    });

    return handleResponse(response);
  }

  async notifications(
    params?: NotificationsGetNotificationsParams
  ): Promise<NotificationsListResponse> {
    const baseUrl = await this.getBaseUrl();
    const path = normalizeUrl(getNotificationsGetNotificationsUrl(params));
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`);
    return handleResponse(response);
  }

  async periodicTasks(
    params?: TasksGetPeriodicTasksParams
  ): Promise<PeriodicTasksListResponse> {
    const baseUrl = await this.getBaseUrl();
    const path = normalizeUrl(getTasksGetPeriodicTasksUrl(params));
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`);
    return handleResponse(response);
  }

  async periodicTaskById(id: string): Promise<PeriodicTask> {
    const baseUrl = await this.getBaseUrl();
    const path = normalizeUrl(getTasksGetPeriodicTaskByIdUrl(id));
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`);
    return handleResponse(response);
  }

  async startPeriodicTasks(ids: string[]): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const path = normalizeUrl(getTasksStartPeriodicTaskUrl());

    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        task_ids: ids,
      }),
    });

    return handleVoidResponse(response);
  }

  async completePeriodicTasks(ids: string[]): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const path = normalizeUrl(getTasksCompletePeriodicTaskUrl());

    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        task_ids: ids,
      }),
    });

    return handleVoidResponse(response);
  }

  async statisticsCompliancy(): Promise<StatisticsCompliancyResponse> {
    const baseUrl = await this.getBaseUrl();
    const path = normalizeUrl(getStatisticsGetCompliancyStatisticsUrl());
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`);
    return handleResponse(response);
  }

  async statisticsTaskTotals(): Promise<StatisticsTaskTotals> {
    const baseUrl = await this.getBaseUrl();
    const path = normalizeUrl(getStatisticsGetTaskTotalsUrl());
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`);
    return handleResponse(response);
  }
}
